<template>
  <div>
    <v-form @submit.prevent="submit"
            autocomplete="off"
            :disabled="form.busy">
      <v-card class="mb-4">
        <v-card-title>
          Step 2: Who is the recipient?
        </v-card-title>
        <template v-if="isMyself">
          <v-alert v-if="canEmailSelf"
                   type="info"
                   tile>
            Avoid sending emails to yourself because they could go to your junk folder.
          </v-alert>
          <v-alert v-else
                   type="error"
                   tile>
            You are not allowed to send emails to yourself.
          </v-alert>
        </template>
        <v-card-text class="pt-0">
          <v-row>
            <v-col cols="12">
              <v-text-field :value="template ? template.name : 'None'"
                            label="Template"
                            outlined
                            dense
                            hide-details
                            disabled>
              </v-text-field>
            </v-col>
          </v-row>

          <template v-if="searchError">
            <v-alert type="error"
                     tile>
              There was an error. Please click the button to reload and try again.<br/>
              If it happens more than once, please contact support@mobilelocker.com<br/>
              <strong>Error message: {{ searchError.message }}</strong>
            </v-alert>

            <v-btn :href="restartURL"
                   color="primary">
              Reload
            </v-btn>
          </template>
          <template v-else>
            <v-row>
              <v-col cols="12">
                <v-combobox v-model="contactObject"
                            ref="contactCombo"
                            autocomplete="off"
                            :disabled="form.busy"
                            :loading="loading"
                            label="Who do you want to email?"
                            outlined
                            dense
                            placeholder="Search for your recipient or type an email address and press ENTER."
                            :autofocus="!$root.isMobile"
                            :search-input.sync="search"
                            :items="normalizedContacts"
                            item-value="id"
                            item-text="name_email_company_record_type"
                            return-object
                            clearable
                            hide-details
                            @click:clear="clearContact"
                            @change="checkContact">
                  <template v-if="isString(search) && normalizedContacts.length > 0 && isEmailInContacts(search) === false"
                            v-slot:append-item>
                    <v-list-item v-if="canCreateContact"
                                 @click.prevent="addNewContact">
                      <strong>"{{ search }}" not found, click here to add as a new contact.</strong>
                    </v-list-item>
                    <v-list-item v-else-if="isAllergan">
                      <div class="mr-4">
                        <strong>"{{ search }}" not found in {{ crmLabel }}.</strong>
                      </div>
                      <div>
                        If you just added or changed an email address in {{ crmLabel }}, <a href="#" @click.prevent="syncFromMoxie">import from {{ crmLabel }}</a>.
                      </div>
                    </v-list-item>
                    <v-list-item v-else>
                      <strong>"{{ search }}" not found.</strong>
                    </v-list-item>
                  </template>
                  <template v-else-if="isString(search)"
                            v-slot:no-data>
                    <v-list-item v-if="canCreateContact"
                                 @click.prevent="addNewContact">
                      <strong>"{{ search }}" not found, click here to add as a new contact.</strong>
                    </v-list-item>
                    <v-list-item v-else-if="isAllergan">
                      <div class="mr-4">
                        <strong>"{{ search }}" not found in {{ crmLabel }}.</strong>
                      </div>
                      <div>
                        If you just added or changed an email address in {{ crmLabel }}, <a href="#" @click.prevent="syncFromMoxie">import from {{ crmLabel }}</a>.
                      </div>
                    </v-list-item>
                    <v-list-item v-else>
                      <strong>"{{ search }}" not found.</strong>
                    </v-list-item>
                  </template>
                </v-combobox>
              </v-col>
            </v-row>

          </template>

        </v-card-text>
      </v-card>
      <v-card v-if="!searchError && isObject(contactForm)"
              :loading="form.busy">
        <v-card-text class="pb-0">
          <UserContactForm v-if="contactForm"
                           :contact="form.contact"
                           :form="contactForm"
                           @updated="fetchContact">
          </UserContactForm>
          <v-alert v-if="contactForm && contactForm.is_active === false"
                   type="warning"
                   dense
                   flat
                   tile
                   class="mt-2 mb-0">
            You need to activate this contact first.
          </v-alert>
          <InvalidEmailAlert v-if="emailVerificationStatus?.is_invalid || didLastMessageBounce"
                             :email="email"
                             :email-verification-status="emailVerificationStatus"
                             :did-last-message-bounce="didLastMessageBounce">
          </InvalidEmailAlert>
        </v-card-text>
        <v-card-actions v-if="!searchError"
                        class="px-4">
          <v-btn @click.prevent="goBack"
                 :disabled="form.busy">
            Previous
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn v-if="isContactAnObject && !form.contact.id"
                 type="submit"
                 color="primary"
                 :disabled="form.busy || (contactForm && contactForm.busy) || (contactForm && contactForm.is_active === false)">
            Save New Contact
          </v-btn>
          <v-btn v-else
                 type="submit"
                 color="primary"
                 :disabled="isUnsubscribed || form.busy || !isContactValid || canSendEmail === false ||  (contactForm && contactForm.is_active === false)">
            Next
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>

    <v-dialog v-model="showSyncDialog"
              max-width="600px"
              persistent>
      <v-card>
        <v-toolbar color="primary"
                   dark
                   flat
                   tile
                   class="d-flex justify-center">
          <v-toolbar-title>
            Importing your contacts from {{ crmLabel }}. Please wait.
          </v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pa-6">
          <p class="d-flex justify-center">
            <v-progress-circular size="64"
                                 indeterminate>
            </v-progress-circular>
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import {Form} from 'vform';
import {mapGetters} from 'vuex';
import {clone, isArray, isInteger, isNull, isObject, isString} from 'lodash';
import userContactService from '../../../services/userContactService';
import validationService from '../../../services/validationService';
import UserContactForm from '../../user-contacts/components/UserContactForm';
import moment from 'moment-timezone';
import echo from '../../../plugins/echo';
import DOMPurify from 'dompurify';
import neverbounceService from '../../../services/neverbounceService';
import emailVerificationService from '../../../services/emailVerificationService';
import InvalidEmailAlert from '../components/InvalidEmailAlert.vue';

export default {
  components: {InvalidEmailAlert, UserContactForm},
  data() {
    return {
      contactObject: null,
      contactForm: null,
      contacts: [],
      form: new Form({
        contact: null,
      }),
      loading: false,
      search: '',
      searchError: false, // MLD-4882
      showSyncDialog: false, // MLD-3237
      email: null, // MLD-6016
      emailVerificationStatus: null, // MLD-6016
      didLastMessageBounce: false, // MLD-6336
    }
  },
  computed: {
    ...mapGetters([
      'canEmailSelf', // MLD-5505
      'isAdmin',
      'isMember',
      'canCreateContact', // MLD-3127
      'canVerifyEmails', // MLD-6016
      'crmLabel',
      'team',
      'user',
    ]),
    ...mapGetters('emailCart', [
      'cart',
      'contact',
      'template',
    ]),
    canSendEmail() { // MLD-5505
      if (isObject(this.contactForm) && isString(this.contactForm.email)) {
        if (this.canEmailSelf === false && this.isMyself) {
          return false;
        }
        return this.isSubscribed;
      }
      return false;
    },
    isMyself() { // MLD-5505
      if (isObject(this.contactForm) && isString(this.contactForm.email)) {
        const email = this.contactForm.email.toLowerCase().trim();
        return email === this.user.email;
      }
      return false;
    },
    isAllergan() { // MLD-3127
      return this.team
        && this.team.id === 194;
    },
    normalizedContacts() {
      return this.contacts;
    },
    isContactAnObject() {
      return isObject(this.form.contact);
    },
    isContactValid() {
      return !!(isObject(this.form.contact)
        && this.form.contact.id
        && this.form.contact.email);
    },
    isSubscribed() { // MLD-2844
      return !this.isUnsubscribed;
    },
    isUnsubscribed() { // MLD-2844
      if (this.isContactAnObject) {
        return !!this.form.contact.is_unsubscribed
      }
      return false;
    },
    restartURL() { // MLD-4882
      if (this.$root.isMobileRoute) {
        return `/v2/mobile/emails/wizard/?template_id=${this.template?.id}`
      }
      return `/v2/app/content/emails/wizard/?template_id=${this.template?.id}`
    },
  },
  watch: {
    search(val) {
      if (isString(val) && val.trim().length > 0) {
        this.searchContacts(val);
      }
    },
  },
  created() {
    this.initEcho(); // MLD-3237
    if (this.contact) {
      this.contactObject = clone(this.contact);
      this.form.contact = clone(this.contact);
      this.checkContact();
      //console.log('reloaded contact from VueX');
    } else if (isString(this.$route.query.email)) {
      this.contactObject = this.$route.query.email;
      this.searchContacts(this.contactObject);
    }
  },
  methods: {
    isString(val) {
      return isString(val);
    },
    isObject(val) {
      return isObject(val);
    },
    initEcho() { // MLD-3237
      echo.private(this.user.channel)
        .listen('CRM.UserContactsImported', e => {
          console.log('CRM.UserContactsImported', e);
          if (e.success) {
            this.$toast.success(DOMPurify.sanitize(e.message));
          } else {
            this.$toast.error(DOMPurify.sanitize(e.message));
          }
          this.showSyncDialog = false;
        });
    },
    fetchContact() { // MLD-2851
      if (this.isContactValid) {
        userContactService.getContact(this.form.contact.id)
          .then(data => {
            this.form.contact = data;
          })
          .catch(() => {
          });
      }
    },
    isEmailInContacts(search) { // MLD-6016
      if (isString(search)) {
        const email = search.toLowerCase().trim();
        if (email) {
          const emails = this.normalizedContacts.map(c => c.email);
          return emails.includes(email);
        }
      }
      return false;
    },
    searchContacts(query) {
      if (isObject(query)) {
        console.log('searchContacts: query is an object so it will not be searched.');
        return;
      }
      if (!query) {
        this.loading = false;
        this.contacts = [];
        return;
      }
      if (this.searchError) { // MLD-4882
        this.loading = false;
        this.contacts = [];
        return;
      }
      this.loading = true;
      const isEmail = validationService.isEmailValid(query);
      const form = {
        email: isEmail ? query : null,
        query: isEmail ? null : query,
        order_by: 'updated_at',
        order_dir: 'desc',
        limit: 30,
      }
      return userContactService.search(form, this.user)
        .then(data => {
          if (isArray(data.hits)) { // MLD-4320 - Meilisearch results
            this.contacts = data.hits;
          } else {
            this.contacts = data;
          }
        })
        .catch(err => { // MLD-4882
          this.searchError = err;
          this.form.contact = null;
          this.contactForm = null;
        })
        .finally(() => {
          this.loading = false;
        })
    },
    addNewContact() {
      this.$refs.contactCombo.blur();
      this.checkContact();
    },
    clearContact() {
      console.log('clearContact()');
      this.contactObject = null;
      this.contactForm = null;
      this.form.contact = null;
      this.$store.dispatch('emailCart/setContact', null);
      this.resetEmailVerificationStatus();
    },
    checkContact() {
      if (isNull(this.contactObject)) {
        return;
      }
      console.log('checkContact()');
      this.resetEmailVerificationStatus(); // MLD-6016
      if (this.canCreateContact && isString(this.contactObject) && this.contactObject.trim()) {
        const contact = {
          id: null,
          greeting_type: 'Formal',
          first_name: '',
          last_name: '',
          designation: '',
          email: this.contactObject,
          name_email: this.contactObject,
          name_email_company: this.contactObject, // MLD-3185
          name_email_company_record_type: this.contactObject, // MLD-3657
          company_name: '',
          sync_from_crm: true,
          is_active: true,
          is_subscribed: true, // MLD-2844
          is_unsubscribed: false, // MLD-2844
          npi: '',
          lists: [],
          external_id: '',
          // MLD-2872
          address1: '',
          address2: '',
          address3: '',
          address4: '',
          locality: '',
          administrative_area: '',
          postal_code: '',
          country: '',
          timezone: '',
          email_is_invalid: false, // MLD-6016
        }
        this.form.contact = contact;
        this.contactForm = new Form(userContactService.initForm(contact));
      } else if (isObject(this.contactObject) && isInteger(this.contactObject.id)) {
        userContactService.getContact(this.contactObject.id) // MLD-4320
          .then(data => {
            this.form.contact = data; // MLD-5124
            this.email = data.email; // MLD-6275
            this.contactForm = new Form(userContactService.initForm(data));
            if (this.form.contact.email_is_invalid) {
              this.makeEmailVerificationStatusInvalid();
            }
          })
          .catch(err => {
            this.contactForm = null;
          })
      } else {
        this.clearContact();
      }
    },
    resetEmailVerificationStatus() { // MLD-6016
      this.email = null;
      this.emailVerificationStatus = null;
      this.didLastMessageBounce = false;
    },
    makeEmailVerificationStatusInvalid() { // MLD-6016
      this.form.busy = false;
      this.emailVerificationStatus = {
        is_invalid: true,
      }
    },
    async submit() {
      this.resetEmailVerificationStatus();

      /*if (!this.form.contact) {
        return false;
      }
      if (isString(this.form.contact)) {
        return false;
      }*/
      if (isObject(this.form.contact) === false) {
        return false;
      }
      if (this.form.contact.is_unsubscribed) { // MLD-2844
        this.$toast.warning('You cannot edit an unsubscribed contact.');
        return false;
      }
      if (this.canEmailSelf === false && this.isMyself) {
        this.$toast.warning('You cannot send an email to yourself.');
        return false;
      }
      this.form.startProcessing();

      if (this.canVerifyEmails && isObject(this.contactForm) && isString(this.contactForm.email)) { // MLD-6016
        this.email = this.contactForm.email;
        try {
          this.emailVerificationStatus = await neverbounceService.verify(this.email);
          if (isObject(this.emailVerificationStatus) && this.emailVerificationStatus.is_invalid === true) {
            this.form.busy = false;
            this.contactForm.errors.set('email', 'Invalid email address.');
            return;
          } else {
            // MLD-6336
            this.didLastMessageBounce = await emailVerificationService.didLastMessageBounce(this.email);
            if (this.didLastMessageBounce) {
              this.form.busy = false;
              this.contactForm.errors.set('email', 'Invalid email address. The last message sent to this address bounced.');
              return;
            }
          }
          // MLD-6544
          this.emailVerificationStatus = await emailVerificationService.verifyEmail(this.email);
          if (isObject(this.emailVerificationStatus) && this.emailVerificationStatus.is_invalid === true) {
            this.form.busy = false;
            this.contactForm.errors.set('email', 'Invalid email address.');
            return;
          }
        } catch (e) {
          this.makeEmailVerificationStatusInvalid();
          this.form.busy = false;
          return;
        }
      }

      try {
        if (isInteger(this.form.contact.id)) {
          // Update the contact
          const {data: contact} = await this.contactForm.put(`/api/user-contacts/${this.form.contact.id}`)
          this.form.contact = contact;
        } else {
          // create the contact
          const {data: contact} = await this.contactForm.post(`/api/user-contacts`)
          this.form.contact = contact;
        }
      } catch (err) {
        this.$toast.warning('The contact could not be saved.');
        this.form.busy = false;
        return;
      }
      if (this.form.contact.email_is_invalid === true) {
        this.email = this.form.contact.email;
        this.makeEmailVerificationStatusInvalid();
        return;
      }
      if (this.didLastMessageBounce) { // MLD-6336
        return;
      }
      if (!this.isContactValid) {
        this.$toast.warning('You have to create or edit the contact first.');
        this.form.busy = false;
        return;
      }

      let contact = (isObject(this.form.contact) && this.form.contact.id)
        ? this.form.contact
        : await userContactService.getContact(this.form.contact.id);
      await this.$store.dispatch('emailCart/setContact', contact);
      await this.$store.dispatch('emailCart/nextStep');
      await this.$router.push({name: `${this.$root.routePrefix}.email.wizard.compose`, query: this.$route.query});
      this.form.finishProcessing();
    },
    goBack() {
      this.contactForm = null;
      //this.$store.dispatch('emailCart/setContact', null);
      this.$store.dispatch('emailCart/prevStep');
      this.$router.replace({name: `${this.$root.routePrefix}.email.wizard.template`, query: this.$route.query});
    },
    syncFromMoxie() { // MLD-3237
      this.showSyncDialog = true;
      const startAt = moment().startOf('day');
      userContactService.syncFromCRM(this.user.id, startAt.format())
        .then(data => {
          if (data.enriching) {

          } else {
            this.$toast.error('Your contacts could not be synced from moxie.');
            this.showSyncDialog = false;
          }
        })
    }
  }
}
</script>
